<script setup lang="ts">
useHead({
  title: 'Akai MPC1000/MPC2500 Player & PGM Maker — Free Tools',
  meta: [
    {
      name: 'description',
      content:
        'A free browser-based MPC1000 player and PGM Maker, plus docs for MPC1000 & MPC2500 owners - built around the real project files these machines produce.',
    },
    { property: 'og:title', content: 'Akai MPC1000/MPC2500 Player & PGM Maker — Free Tools' },
    {
      property: 'og:description',
      content:
        'Browser-based tools, file-format documentation, and workflow guides for MPC1000/MPC2500 owners, built around the real project files these machines produce.',
    },
    // Shared with every other page (see scripts/inject-head.mjs's DEFAULT_IMAGE) until
    // this page gets its own dedicated share image.
    { property: 'og:image', content: 'https://mpc1000.com/og-player.png' },
    { property: 'og:url', content: 'https://mpc1000.com/' },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: 'https://mpc1000.com/og-player.png' },
  ],
})

const faqs: { q: string; a: string }[] = [
  {
    q: 'What is the Akai MPC1000?',
    a: "A standalone hardware sampler and sequencer Akai released in 2004 - 16 pressure-sensitive pads, a small onboard screen, and enough processing to sample, chop, program, and sequence a full track without a computer in the loop. It became a staple of hip-hop and sample-based production, and a huge number of units are still in daily use today, many running the community JJOS operating system instead of Akai's stock firmware.",
  },
  {
    q: 'What does the MPC1000 Player do?',
    a: "It's the free browser tool on this site that opens a real MPC1000 or MPC2500 project folder and lets you browse pads, programs, sequences, and the mixer, preview samples, and export stems - all in your browser, without the hardware connected.",
  },
  {
    q: 'Do I need an actual MPC1000 to use the Player?',
    a: 'No. You just need a project folder - from a CompactFlash card or a backup - and a browser that supports the File System Access API, such as Chrome or Edge. The Player only reads existing project files; the machine itself never needs to be connected.',
  },
  {
    q: 'Does the MPC1000 Player work with JJOS projects?',
    a: 'Yes. The Player and PGM Maker both read and write the same .ALL/.PGM/.WAV files used by stock Akai OS and by JJOS (JJOS2XL, 128XL), so projects from either work the same way.',
  },
  {
    q: 'Is the MPC1000 Player free?',
    a: 'Yes - free to use, with no account or install required, and nothing you load is ever uploaded anywhere.',
  },
]

type Tool = {
  title: string
  status: 'live' | 'beta' | 'soon'
  body: string
  to?: string
}
const tools: Tool[] = [
  {
    title: 'MPC1000 Player',
    status: 'live',
    body: 'Load a real MPC1000 project folder and explore it right in your browser - pads, mixer, chop editor, FX, stems export. JJOS compatible. Nothing ever leaves your machine.',
    to: '/player/',
  },
  {
    title: 'PGM Maker',
    status: 'beta',
    body: 'Build .PGM program files from scratch - assign samples to pads, chop, tune, map MIDI notes - and export a file your MPC1000 can load. JJOS compatible.',
    to: '/pgm-maker/',
  },
  {
    title: 'Bulk Converter & Renamer',
    status: 'beta',
    body: 'Batch-convert samples from any source to the MPC1000 format - 16-bit, 44.1kHz WAV - rename them for the 16-character pad-name limit, then send them straight into the PGM Maker to build a program.',
    to: '/converter/',
  },
  {
    title: 'Docs',
    status: 'beta',
    body: 'Getting-started guides for producing, mixing, and editing on an MPC1000 or MPC2500, covering stock Akai OS and JJOS. Just Getting Started is written so far - more sections on the way.',
    to: '/docs/',
  },
  {
    title: 'Programs / Sample Packs',
    status: 'soon',
    body: 'Original programs and sample packs built for the MPC1000, ready to load straight in.',
  },
]
const statusLabel: Record<Tool['status'], string> = { live: 'Live', beta: 'Beta', soon: 'Soon' }
</script>

<template>
  <div>
    <section class="mx-auto grid max-w-[1120px] grid-cols-[minmax(0,520px)_minmax(360px,460px)] items-center justify-center gap-8 py-8 max-[880px]:grid-cols-1 max-[880px]:gap-8">
      <div>
        <div class="mb-3.5 font-mono text-xs uppercase tracking-[0.18em] text-accent">Est. 2026</div>
        <h1 class="font-display text-[56px] font-bold uppercase leading-[0.98] tracking-tight text-ink max-[520px]:text-[40px]">
          The MPC1000 &amp;<br />MPC2500 <span class="text-phosphor">hub</span>
        </h1>
        <p class="mb-6 mt-4.5 max-w-[46ch] text-sm leading-relaxed text-muted">
          Browser-based tools, file-format documentation, and workflow guides for MPC1000/MPC2500
          owners, built around the real project files these machines produce.
        </p>
        <div class="flex gap-3">
          <NuxtLink
            to="/player/"
            class="rounded px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-wide text-[#1a0f04] bg-accent hover:opacity-90"
          >
            Open the Player →
          </NuxtLink>
          <a
            href="#about-jjos"
            class="rounded border border-border px-5 py-2.5 font-mono text-xs uppercase tracking-wide text-muted hover:border-accent-dim hover:text-ink"
          >
            About JJOS
          </a>
        </div>
      </div>

      <div class="relative h-[430px] max-[880px]:h-[300px]">
        <div
          class="pointer-events-none absolute inset-x-0 -top-[60px] bottom-0"
          style="background: radial-gradient(ellipse 55% 48% at 55% 48%, rgba(255,138,61,0.20), transparent 72%)"
        ></div>
        <picture>
          <source media="(max-width: 720px)" srcset="/mpc2500-400.webp" />
          <img
            src="/mpc2500-800.webp"
            alt="Akai MPC2500"
            width="800"
            height="619"
            fetchpriority="high"
            class="absolute right-[-4%] top-0 z-[1] w-[84%] -rotate-[4deg]"
            style="filter: drop-shadow(0 26px 34px rgba(0,0,0,0.6))"
          />
        </picture>
        <picture>
          <source media="(max-width: 720px)" srcset="/mpc1000-420.webp" />
          <img
            src="/mpc1000-600.webp"
            alt="Akai MPC1000"
            width="600"
            height="363"
            class="absolute bottom-1 left-[-2%] z-[2] w-[66%] rotate-3"
            style="filter: drop-shadow(0 26px 34px rgba(0,0,0,0.6))"
          />
        </picture>
        <div class="absolute inset-x-0 -bottom-[26px] text-center font-mono text-xs uppercase tracking-[0.14em] text-muted-dim">
          MPC1000 &amp; MPC2500
        </div>
      </div>
    </section>

    <section class="mx-auto max-w-[1080px]">
      <h2 class="mb-4 mt-2 flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.14em] text-muted-dim">
        About the MPC1000
        <span class="h-px flex-1 bg-border"></span>
      </h2>
      <p class="mb-3.5 max-w-[74ch] text-sm leading-relaxed text-muted">
        The <strong class="text-ink">Akai MPC1000</strong> is a standalone hardware sampler and
        sequencer Akai released in 2004 - 16 pressure-sensitive pads, a small onboard screen, and
        enough processing to sample, chop, program, and sequence a full track without a computer
        in the loop. It became a staple of hip-hop and sample-based production, and more than two
        decades later a huge number of units are still in daily use.
      </p>
      <p class="mb-13 max-w-[74ch] text-sm leading-relaxed text-muted">
        A large share of those MPC1000s run <a href="#about-jjos" class="text-accent hover:underline">JJOS</a>,
        a community-built replacement OS, rather than Akai's stock firmware, and its bigger
        sibling the <strong class="text-ink">MPC2500</strong> shares the same project-file
        formats. Both save projects as <code class="font-mono text-xs text-ink">.ALL</code>/<code class="font-mono text-xs text-ink">.PGM</code>/<code class="font-mono text-xs text-ink">.WAV</code>
        files on CompactFlash - the same files the MPC1000 Player and PGM Maker below read and
        write directly.
      </p>
    </section>

    <section class="mx-auto max-w-[1080px]">
      <h2 class="mb-4 mt-2 flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.14em] text-muted-dim">
        Tools
        <span class="h-px flex-1 bg-border"></span>
      </h2>

      <div class="screen-frame mb-13">
        <div class="screen" style="padding: 0">
          <template v-for="(tool, i) in tools" :key="tool.title">
            <NuxtLink
              v-if="tool.status !== 'soon'"
              :to="tool.to!"
              class="relative grid grid-cols-[28px_20px_1fr_auto] items-baseline gap-3.5 border-b border-phosphor/12 px-5.5 py-4 no-underline last:border-b-0 hover:bg-phosphor/5"
            >
              <span class="font-mono text-xs text-phosphor/45">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="h-[7px] w-[7px] self-center rounded-full border border-phosphor bg-phosphor shadow-[0_0_8px_rgba(143,245,213,0.8)]"></span>
              <span class="min-w-0">
                <div class="font-screen text-2xl leading-none tracking-wide text-phosphor">{{ tool.title.toUpperCase() }}</div>
                <div class="mt-1 max-w-[62ch] font-sans text-xs leading-[1.55] text-phosphor/50">{{ tool.body }}</div>
              </span>
              <span class="self-center whitespace-nowrap font-mono text-xs uppercase tracking-wide text-phosphor">
                {{ statusLabel[tool.status] }} →
              </span>
            </NuxtLink>
            <div
              v-else
              class="relative grid grid-cols-[28px_20px_1fr_auto] items-baseline gap-3.5 border-b border-phosphor/12 px-5.5 py-4 last:border-b-0"
            >
              <span class="font-mono text-xs text-phosphor/45">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="h-[7px] w-[7px] self-center rounded-full border border-phosphor/40"></span>
              <span class="min-w-0">
                <div class="font-screen text-2xl leading-none tracking-wide text-phosphor/55">{{ tool.title.toUpperCase() }}</div>
                <div class="mt-1 max-w-[62ch] font-sans text-xs leading-[1.55] text-phosphor/50">{{ tool.body }}</div>
              </span>
              <span class="self-center whitespace-nowrap font-mono text-xs uppercase tracking-wide text-phosphor/35">
                {{ statusLabel[tool.status] }}
              </span>
            </div>
          </template>
        </div>
      </div>
    </section>

    <section class="mx-auto max-w-[1080px]">
      <h2 class="mb-4 mt-2 flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.14em] text-muted-dim">
        The MPC1000 Player
        <span class="h-px flex-1 bg-border"></span>
      </h2>
      <p class="mb-3.5 max-w-[74ch] text-sm leading-relaxed text-muted">
        The <NuxtLink to="/player/" class="text-accent hover:underline">MPC1000 Player</NuxtLink>
        is a free browser-based tool that loads a real MPC1000 or MPC2500 project folder and lets
        you explore it without touching the hardware - browse programs and pads, preview samples,
        inspect the mixer and sequences, and export stems. It reads the same
        <code class="font-mono text-xs text-ink">.ALL</code>/<code class="font-mono text-xs text-ink">.PGM</code>/<code class="font-mono text-xs text-ink">.WAV</code>
        files the machine itself produces, works with both stock Akai OS and JJOS project files,
        and needs nothing installed - just a folder and a browser that supports the File System
        Access API.
      </p>
      <p class="mb-13 max-w-[74ch] text-sm leading-relaxed text-muted">
        It's useful any time you need to look inside a project without the unit in front of you:
        checking what's on a CompactFlash card before buying or selling gear, reviewing a
        bandmate's project remotely, or archiving old work. Everything runs locally - a loaded
        project is read straight off disk in the browser and never uploaded anywhere.
      </p>
    </section>

    <section id="about-jjos" class="relative mx-auto mt-8.5 max-w-[1080px] rounded-lg border border-border px-7 py-6.5">
      <span class="absolute -top-2.5 left-5.5 bg-chassis px-2 font-mono text-xs uppercase tracking-[0.12em] text-muted-dim">Notes</span>
      <p class="max-w-[74ch] text-xs leading-relaxed text-muted">
        Most MPC1000/MPC2500 owners run <strong class="text-ink">JJOS</strong>, a replacement OS
        that's still actively developed and far more capable than Akai's stock firmware.
        MPC1000.com is <strong class="text-ink">not JJOS</strong>, isn't affiliated with or
        endorsed by it, and isn't trying to replace it - everything here is browser-based tooling
        and documentation that works with the files a JJOS-powered machine already produces. All
        credit for the actual OS goes to JJ, its creator and sole developer.
      </p>
      <a
        href="https://www7a.biglobe.ne.jp/~mpc1000/128xl/manual.htm"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-3.5 inline-block font-mono text-xs font-semibold text-accent hover:underline"
      >
        JJOS 128XL documentation ↗
      </a>
    </section>

    <section class="mx-auto mt-13 max-w-[1080px]">
      <h2 class="mb-4 flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.14em] text-muted-dim">
        FAQ
        <span class="h-px flex-1 bg-border"></span>
      </h2>
      <dl class="grid gap-6">
        <div v-for="faq in faqs" :key="faq.q">
          <dt class="text-sm font-semibold text-ink">{{ faq.q }}</dt>
          <dd class="mt-1.5 max-w-[74ch] text-sm leading-relaxed text-muted">{{ faq.a }}</dd>
        </div>
      </dl>
    </section>
  </div>
</template>
